import React, { useEffect, useState } from 'react';
import apiService, { qualityControlV2Api } from '../services/api.service';
import { ProjectAutocomplete } from '../components/ProjectAutocomplete';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Collapse,
  Tooltip,
  Tabs,
  Tab,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

// ==================== INTERFACES ====================

interface OperatorStats {
  operator_name: string;
  total_calls: number;
  average_total_score: number;
  average_greeting_score: number;
  average_needs_score: number;
  average_presentation_score: number;
  average_objection_score: number;
  average_closing_score: number;
  greeting_dropout_percent: number;
  needs_dropout_percent: number;
  presentation_dropout_percent: number;
  objection_dropout_percent: number;
  closing_dropout_percent: number;
  // Metrics/Extended fields
  average_retention_score?: number;
  average_communication_quality_score?: number;
  average_call_duration_seconds: number;
  average_talk_ratio_operator: number;
  average_interruptions_count: number;
  average_filler_words_per_100: number;
  most_common_errors: { code: string; name: string; count: number }[];
  primary_weakness: string | null;
  weakness_stage: string | null;
  checklist_compliance_rate: number;
  objection_success_rate: number;
  dynamics: {
    total_score_change: number;
    direction: 'up' | 'down' | 'stable';
  };
}

interface Project {
  id: number;
  name: string;
  project_id: number;
}

interface CallDetails {
  id: number;
  call_id: number;
  operator_name: string;
  project_id: number;
  call_duration_seconds: number;
  transcript: string;
  call_outcome: string;
  greeting_score: number;
  greeting_explanation: string;
  greeting_dropout: boolean;
  needs_identification_score: number | null;
  needs_identification_explanation: string;
  needs_identification_clarified: boolean;
  presentation_score: number | null;
  presentation_explanation: string;
  presentation_script_match: number | null;
  objection_handling_score: number | null;
  objection_handling_explanation: string;
  objection_reworked: boolean;
  closing_score: number | null;
  closing_explanation: string;
  closing_action_type: string;
  closing_action_quality: string;
  total_score: number;
  model_name: string;
  created_at: string;
  mp3_url?: string;
  // Metrics fields
  retention_score: number | null;
  retention_explanation: string;
  communication_quality_score: number | null;
  communication_quality_explanation: string;
  talk_ratio_operator: number;
  interruptions_count: number;
  filler_words_per_100: number;
  script_reading_detected: boolean;
  politeness_markers_present: boolean;
  first_refusal_handled: boolean;
  early_drop: boolean;
  next_step_recorded: boolean;
  detected_errors: string[];
  technical_issues: string[];
  objections_encountered: string[];
  objections_handled: string[];
  primary_weakness: string | null;
  weakness_stage: string | null;
  dropout_stage: string | null;
  ai_recommendation: string | null;
  checklist_result: {
    total: number;
    fulfilled: number;
    missing: string[];
  } | null;
}

interface DropoutAnomalies {
  greeting: boolean;
  retention: boolean;
  needs: boolean;
  objection: boolean;
  presentation: boolean;
  communication_quality: boolean;
  closing: boolean;
}

interface ErrorAnalysis {
  most_common_errors: {
    code: string;
    name: string;
    category: string;
    count: number;
    percent: number;
  }[];
  errors_by_category: {
    category: string;
    count: number;
    percent: number;
  }[];
  operators_by_error: {
    code: string;
    operators: { name: string; count: number }[];
  }[];
}

interface TechnicalAnalysis {
  most_common_issues: {
    code: string;
    name: string;
    count: number;
    percent: number;
  }[];
  operators_by_issue: {
    code: string;
    operators: { name: string; count: number }[];
  }[];
}

interface ObjectionsAnalysis {
  objection_stats: {
    objection: string;
    total_encountered: number;
    total_handled: number;
    success_rate: number;
  }[];
  best_handlers: {
    objection: string;
    operator_name: string;
    success_rate: number;
  }[];
}

interface TeamDiagnosis {
  primary_issues: {
    stage: string;
    issue: string;
    affected_operators: string[];
    severity: 'low' | 'medium' | 'high' | 'critical';
  }[];
  overall_weakness_distribution: {
    stage: string;
    percent: number;
  }[];
}

// ==================== HELPER FUNCTIONS ====================

const stageNameRu: Record<string, string> = {
  greeting: 'Приветствие',
  retention: 'Удержание',
  needs: 'Выявление потребности',
  objection: 'Возражения',
  presentation: 'Презентация',
  communication_quality: 'Качество связи',
  closing: 'Закрытие',
  needs_identification: 'Выявление потребности',
  objection_handling: 'Возражения',
};

const categoryNameRu: Record<string, string> = {
  etiquette: 'Этикет',
  needs: 'Потребности',
  objections: 'Возражения',
  script: 'Скрипт',
  technical: 'Технические',
  other: 'Прочее',
};

const severityColor: Record<string, string> = {
  low: '#4caf50',
  medium: '#ff9800',
  high: '#f44336',
  critical: '#9c27b0',
};

// Справочник кодов ошибок → названия
const errorCodeNames: Record<string, string> = {
  // Этикет
  ETQ_01: 'Не называет компанию',
  ETQ_02: 'Не уточняет имя клиента',
  ETQ_03: 'Ошибается в названии компании',
  ETQ_04: 'Сброс после приветствия без перезвона',
  ETQ_05: 'Не эмоциональная подача',
  ETQ_06: 'Не прощается в конце разговора',
  // Триггеры (Hard Penalties)
  TRG_01: 'Фраза "Вас беспокоят"',
  TRG_02: 'Фраза "Уделите минуту"',
  TRG_03: 'Фраза "Не займу много времени"',
  TRG_04: 'Фраза "Это реклама"',
  TRG_05: 'Фраза "Мы гарантируем"',
  TRG_06: 'Фраза "Точно получите"',
  // Выявление потребности
  NDS_01: 'Не спрашивает город',
  NDS_02: 'Не задаёт уточняющие вопросы',
  NDS_03: 'Не спрашивает точный адрес',
  NDS_04: 'Не спрашивает ценовой диапазон/бюджет',
  NDS_05: 'Не уточняет сроки покупки',
  NDS_06: 'Не уточняет мощность/характеристики',
  NDS_07: 'Не слушает клиента внимательно',
  NDS_08: 'Не уточняет способ оплаты',
  NDS_09: 'Не уточняет, удобно ли разговаривать',
  NDS_10: 'Не уточняет, когда перезвонить',
  // Возражения
  OBJ_01: 'Бросает трубку на "не интересно"',
  OBJ_02: 'Не отрабатывает возражения',
  OBJ_03: 'Отрабатывает ложные возражения',
  // Скрипт
  SCR_01: 'Обещает бесплатное без уточнения',
  SCR_02: 'Отклонился от скрипта',
  SCR_03: 'Передал не квалифицированного клиента',
  SCR_04: 'Не уточнил критерии чек-листа',
  // Качество коммуникации
  COM_01: 'Перебивание клиента',
  COM_02: 'Слова-паразиты (высокая частота)',
  COM_03: 'Монолог (высокий Talk Ratio)',
  COM_04: 'Чтение по скрипту (роботизированность)',
  // Технические
  TECH_01: 'Плохая связь',
  TECH_02: 'Большие паузы',
  TECH_03: 'Жаргон/непонятные термины',
  TECH_04: 'Проблемы с громкостью',
};

const getErrorName = (code: string): string => errorCodeNames[code] || code;

// Типы завершения звонка
const callOutcomeNames: Record<string, { label: string; color: string; description: string }> = {
  client_busy: { 
    label: 'Клиент занят', 
    color: '#2196f3',
    description: 'Клиент не мог говорить, договорились о перезвоне' 
  },
  client_not_interested: { 
    label: 'Не заинтересован', 
    color: '#ff9800',
    description: 'Клиент отказался от предложения' 
  },
  successful: { 
    label: 'Успешный', 
    color: '#4caf50',
    description: 'Получены данные клиента' 
  },
  callback_scheduled: { 
    label: 'Перезвон', 
    color: '#9c27b0',
    description: 'Договорились о перезвоне в удобное время' 
  },
  failed: { 
    label: 'Провал', 
    color: '#f44336',
    description: 'Звонок завершился неудачно по вине оператора' 
  },
};

const formatDuration = (seconds: number): string => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// ==================== MAIN COMPONENT ====================

export const QualityControlV2Page: React.FC = () => {
  // State
  const [operators, setOperators] = useState<OperatorStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anomalies, setAnomalies] = useState<Record<string, DropoutAnomalies>>({});
  const [activeTab, setActiveTab] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Analysis data
  const [errorAnalysis, setErrorAnalysis] = useState<ErrorAnalysis | null>(null);
  const [technicalAnalysis, setTechnicalAnalysis] = useState<TechnicalAnalysis | null>(null);
  const [objectionsAnalysis, setObjectionsAnalysis] = useState<ObjectionsAnalysis | null>(null);
  const [teamDiagnosis, setTeamDiagnosis] = useState<TeamDiagnosis | null>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 28);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  // Expanded rows
  const [expandedOperator, setExpandedOperator] = useState<string | null>(null);
  const [operatorCalls, setOperatorCalls] = useState<CallDetails[]>([]);
  const [loadingCalls, setLoadingCalls] = useState(false);

  // Call details modal
  const [selectedCall, setSelectedCall] = useState<CallDetails | null>(null);
  const [callModalOpen, setCallModalOpen] = useState(false);

  // Audio playback state
  const [playingAudio, setPlayingAudio] = useState<number | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [audioProgress, setAudioProgress] = useState<{ [key: number]: number }>({});
  const [audioDuration, setAudioDuration] = useState<{ [key: number]: number }>({});
  const [playbackSpeed] = useState<number>(1);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Load data when filters change
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedProjectId]);

  // Load analysis when tab changes
  useEffect(() => {
    if (activeTab === 1) loadErrorAnalysis();
    if (activeTab === 2) loadTechnicalAnalysis();
    if (activeTab === 3) loadObjectionsAnalysis();
    if (activeTab === 4) loadTeamDiagnosis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, startDate, endDate, selectedProjectId]);

  const loadProjects = async () => {
    try {
      const res = await apiService.get('/projects');
      setProjects(res.data || []);
    } catch (e) {
      console.error('Failed to load projects:', e);
    }
  };

  const loadErrorAnalysis = async () => {
    setLoadingAnalysis(true);
    try {
      const res = await qualityControlV2Api.getErrorAnalysis(
        startDate,
        endDate,
        selectedProjectId || undefined
      );
      setErrorAnalysis(res.data);
    } catch (e) {
      console.error('Failed to load error analysis:', e);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const loadTechnicalAnalysis = async () => {
    setLoadingAnalysis(true);
    try {
      const res = await qualityControlV2Api.getTechnicalAnalysis(
        startDate,
        endDate,
        selectedProjectId || undefined
      );
      setTechnicalAnalysis(res.data);
    } catch (e) {
      console.error('Failed to load technical analysis:', e);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const loadObjectionsAnalysis = async () => {
    setLoadingAnalysis(true);
    try {
      const res = await qualityControlV2Api.getObjectionsAnalysis(
        startDate,
        endDate,
        selectedProjectId || undefined
      );
      setObjectionsAnalysis(res.data);
    } catch (e) {
      console.error('Failed to load objections analysis:', e);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const loadTeamDiagnosis = async () => {
    setLoadingAnalysis(true);
    try {
      const res = await qualityControlV2Api.getTeamDiagnosis(
        startDate,
        endDate,
        selectedProjectId || undefined
      );
      setTeamDiagnosis(res.data);
    } catch (e) {
      console.error('Failed to load team diagnosis:', e);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  // Audio playback functions
  const handlePlayAudio = (callId: number, mp3Url: string) => {
    if (playingAudio === callId) {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      setPlayingAudio(null);
      setAudioElement(null);
      setAudioProgress(prev => ({ ...prev, [callId]: 0 }));
    } else {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      
      const audio = new Audio(mp3Url);
      audio.playbackRate = playbackSpeed;
      
      audio.addEventListener('loadedmetadata', () => {
        setAudioDuration(prev => ({ ...prev, [callId]: audio.duration }));
      });
      
      audio.addEventListener('timeupdate', () => {
        setAudioProgress(prev => ({ ...prev, [callId]: audio.currentTime }));
      });
      
      audio.addEventListener('ended', () => {
        setPlayingAudio(null);
        setAudioElement(null);
        setAudioProgress(prev => ({ ...prev, [callId]: 0 }));
      });
      
      audio.addEventListener('error', () => {
        console.error('Ошибка воспроизведения аудио');
        setPlayingAudio(null);
        setAudioElement(null);
      });
      
      audio.play();
      setPlayingAudio(callId);
      setAudioElement(audio);
    }
  };

  const handleSeek = (callId: number, newTime: number) => {
    if (audioElement && playingAudio === callId) {
      audioElement.currentTime = newTime;
      setAudioProgress(prev => ({ ...prev, [callId]: newTime }));
    }
  };

  const handleDownloadAudio = (mp3Url: string, callId: number) => {
    const link = document.createElement('a');
    link.href = mp3Url;
    link.download = `call_${callId}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    };
  }, [audioElement]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });
      if (selectedProjectId) {
        params.append('project_id', selectedProjectId.toString());
      }

      // Load operators scoreboard
      const res = await apiService.get(`/quality-control-v2/operators?${params.toString()}`);
      setOperators(res.data || []);

      // Load dropout analysis for anomaly detection
      const dropoutRes = await apiService.get(
        `/quality-control-v2/dropout-analysis?${params.toString()}`,
      );
      const dropoutData = dropoutRes.data;

      // Build anomaly map
      const anomalyMap: Record<string, DropoutAnomalies> = {};
      if (dropoutData?.operator_anomalies) {
        for (const anomaly of dropoutData.operator_anomalies) {
          if (!anomalyMap[anomaly.operator_name]) {
            anomalyMap[anomaly.operator_name] = {
              greeting: false,
              retention: false,
              needs: false,
              objection: false,
              presentation: false,
              communication_quality: false,
              closing: false,
            };
          }
          if (anomaly.stage === 'greeting') anomalyMap[anomaly.operator_name].greeting = true;
          if (anomaly.stage === 'retention') anomalyMap[anomaly.operator_name].retention = true;
          if (anomaly.stage === 'needs') anomalyMap[anomaly.operator_name].needs = true;
          if (anomaly.stage === 'objection') anomalyMap[anomaly.operator_name].objection = true;
          if (anomaly.stage === 'presentation')
            anomalyMap[anomaly.operator_name].presentation = true;
          if (anomaly.stage === 'communication_quality')
            anomalyMap[anomaly.operator_name].communication_quality = true;
          if (anomaly.stage === 'closing') anomalyMap[anomaly.operator_name].closing = true;
        }
      }
      setAnomalies(anomalyMap);
    } catch (e: any) {
      setError('Не удалось загрузить данные операторов');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadData();
  };

  const handleResetFilters = () => {
    const date = new Date();
    date.setDate(date.getDate() - 28);
    setStartDate(date.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setSelectedProjectId(null);
  };

  const handleExpandOperator = async (operatorName: string) => {
    if (expandedOperator === operatorName) {
      setExpandedOperator(null);
      setOperatorCalls([]);
    } else {
      setExpandedOperator(operatorName);
      setLoadingCalls(true);
      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          operator_name: operatorName,
          limit: '50',
          offset: '0',
        });
        const res = await apiService.get(`/quality-control-v2/calls?${params.toString()}`);
        setOperatorCalls(res.data?.data || []);
      } catch (e) {
        console.error('Failed to load operator calls:', e);
      } finally {
        setLoadingCalls(false);
      }
    }
  };

  const handleOpenCallDetails = (call: CallDetails) => {
    setSelectedCall(call);
    setCallModalOpen(true);
  };

  const renderDynamics = (dynamics: OperatorStats['dynamics']) => {
    if (dynamics.direction === 'stable') {
      return <Chip label="→ 0.0" size="small" sx={{ bgcolor: '#e0e0e0' }} />;
    }
    if (dynamics.direction === 'up') {
      return (
        <Chip
          icon={<ArrowUpwardIcon />}
          label={`+${formatScore(dynamics.total_score_change, 1)}`}
          size="small"
          color="success"
        />
      );
    }
    return (
      <Chip
        icon={<ArrowDownwardIcon />}
        label={formatScore(dynamics.total_score_change, 1)}
        size="small"
        color="error"
      />
    );
  };

  const renderStageCell = (
    score: number,
    dropoutPercent: number,
    isAnomaly: boolean,
  ) => {
    return (
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography variant="body2" fontWeight="bold">
          {formatScore(score, 0)}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="caption" color="text.secondary">
            ({formatScore(dropoutPercent, 0)}%)
          </Typography>
          {isAnomaly && (
            <Tooltip title="Аномалия: выбивается из нормы на ±20%">
              <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#4caf50';
    if (score >= 60) return '#ff9800';
    return '#f44336';
  };

  // Safely format numeric values (handles both numbers and strings from DB)
  const formatScore = (value: number | string | null | undefined, decimals: number = 0): string => {
    if (value == null) return '0';
    const num = Number(value);
    return isNaN(num) ? '0' : num.toFixed(decimals);
  };

  // ==================== RENDER TABS ====================

  const renderOperatorsTab = () => (
    <>
      {/* Operators Table */}
      {!loading && operators.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Таблица операторов
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell><strong>Оператор</strong></TableCell>
                    <TableCell align="center"><strong>Звонков</strong></TableCell>
                    <TableCell align="center"><strong>Ср. время</strong></TableCell>
                    <TableCell align="center"><strong>Общий балл</strong></TableCell>
                    <TableCell align="center"><strong>Приветствие</strong></TableCell>
                    <TableCell align="center"><strong>Потребности</strong></TableCell>
                    <TableCell align="center"><strong>Презентация</strong></TableCell>
                    <TableCell align="center"><strong>Возражения</strong></TableCell>
                    <TableCell align="center"><strong>Закрытие</strong></TableCell>
                    <TableCell align="center"><strong>Метрики</strong></TableCell>
                    <TableCell align="center"><strong>Слабость</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operators.map((op) => (
                    <React.Fragment key={op.operator_name}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleExpandOperator(op.operator_name)}
                          >
                            {expandedOperator === op.operator_name ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{op.operator_name}</Typography>
                            {op.most_common_errors?.length > 0 && (
                              <Tooltip title={`Частые ошибки: ${op.most_common_errors.map(e => e.name).join(', ')}`}>
                                <Chip 
                                  icon={<ErrorIcon />} 
                                  label={op.most_common_errors.length} 
                                  size="small" 
                                  color="warning" 
                                  sx={{ mt: 0.5, fontSize: 10 }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{op.total_calls}</TableCell>
                        <TableCell align="center">
                          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {formatDuration(op.average_call_duration_seconds)}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Ratio: {formatScore(op.average_talk_ratio_operator, 0)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ color: getScoreColor(parseFloat(op.average_total_score as any) || 0) }}
                            >
                              {formatScore(op.average_total_score, 0)}
                            </Typography>
                            {renderDynamics(op.dynamics)}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {renderStageCell(
                            op.average_greeting_score,
                            op.greeting_dropout_percent,
                            anomalies[op.operator_name]?.greeting || false,
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {renderStageCell(
                            op.average_needs_score,
                            op.needs_dropout_percent,
                            anomalies[op.operator_name]?.needs || false,
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {renderStageCell(
                            op.average_presentation_score,
                            op.presentation_dropout_percent,
                            anomalies[op.operator_name]?.presentation || false,
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" flexDirection="column" alignItems="center">
                            {renderStageCell(
                              op.average_objection_score,
                              op.objection_dropout_percent,
                              anomalies[op.operator_name]?.objection || false,
                            )}
                            {op.objection_success_rate > 0 && (
                              <Typography variant="caption" color="success.main">
                                ✓{formatScore(op.objection_success_rate, 0)}%
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {renderStageCell(
                            op.average_closing_score,
                            op.closing_dropout_percent,
                            anomalies[op.operator_name]?.closing || false,
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <Typography variant="body2" fontWeight="bold">
                              {formatScore(op.average_talk_ratio_operator, 0)}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Fill: {formatScore(op.average_filler_words_per_100, 1)}
                            </Typography>
                            {op.average_interruptions_count > 0 && (
                              <Typography variant="caption" color="error.main">
                                Int: {formatScore(op.average_interruptions_count, 1)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {op.primary_weakness && (
                            <Tooltip title={op.primary_weakness}>
                              <Chip
                                label={stageNameRu[op.weakness_stage || ''] || op.weakness_stage || '—'}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded Details */}
                      {expandedOperator === op.operator_name && (
                        <TableRow>
                          <TableCell colSpan={11} sx={{ py: 0 }}>
                            <Collapse in={true} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                <Typography variant="h6" gutterBottom>
                                  Звонки оператора: {op.operator_name}
                                </Typography>
                                {loadingCalls ? (
                                  <CircularProgress size={24} />
                                ) : operatorCalls.length > 0 ? (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Дата</TableCell>
                                        <TableCell align="center">Запись</TableCell>
                                        <TableCell align="center">Общий</TableCell>
                                        <TableCell align="center">Привет.</TableCell>
                                        <TableCell align="center">Потр.</TableCell>
                                        <TableCell align="center">През.</TableCell>
                                        <TableCell align="center">Возр.</TableCell>
                                        <TableCell align="center">Закр.</TableCell>
                                        <TableCell align="center">Talk %</TableCell>
                                        <TableCell align="center">Ошибки</TableCell>
                                        <TableCell align="center">Действия</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {operatorCalls.map((call) => (
                                        <TableRow key={call.id} hover>
                                          <TableCell>
                                            {new Date(call.created_at).toLocaleString('ru-RU')}
                                          </TableCell>
                                          <TableCell align="center">
                                            {call.mp3_url ? (
                                              <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => handlePlayAudio(call.id, call.mp3_url!)}
                                                    sx={{
                                                      color: playingAudio === call.id ? '#f44336' : '#4caf50',
                                                    }}
                                                  >
                                                    {playingAudio === call.id ? <StopIcon /> : <PlayArrowIcon />}
                                                  </IconButton>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => handleDownloadAudio(call.mp3_url!, call.id)}
                                                    sx={{ color: '#2196f3' }}
                                                  >
                                                    <DownloadIcon fontSize="small" />
                                                  </IconButton>
                                                </Box>
                                                {playingAudio === call.id && audioDuration[call.id] && (
                                                  <Box sx={{ width: '100%', minWidth: 100 }}>
                                                    <input
                                                      type="range"
                                                      min="0"
                                                      max={audioDuration[call.id]}
                                                      value={audioProgress[call.id] || 0}
                                                      onChange={(e) => handleSeek(call.id, parseFloat(e.target.value))}
                                                      style={{ width: '100%', height: '4px', cursor: 'pointer' }}
                                                    />
                                                    <Typography variant="caption" sx={{ fontSize: '10px' }}>
                                                      {formatTime(audioProgress[call.id] || 0)} / {formatTime(audioDuration[call.id])}
                                                    </Typography>
                                                  </Box>
                                                )}
                                              </Box>
                                            ) : '—'}
                                          </TableCell>
                                          <TableCell align="center">
                                            <Typography fontWeight="bold" sx={{ color: getScoreColor(call.total_score) }}>
                                              {formatScore(call.total_score, 0)}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">{formatScore(call.greeting_score, 0)}</TableCell>
                                          <TableCell align="center">{formatScore(call.needs_identification_score, 0)}</TableCell>
                                          <TableCell align="center">{formatScore(call.presentation_score, 0)}</TableCell>
                                          <TableCell align="center">{formatScore(call.objection_handling_score, 0)}</TableCell>
                                          <TableCell align="center">{formatScore(call.closing_score, 0)}</TableCell>
                                          <TableCell align="center">{formatScore(call.talk_ratio_operator, 0)}%</TableCell>
                                          <TableCell align="center">
                                            {(call.detected_errors?.length || 0) > 0 && (
                                              <Chip
                                                icon={<ErrorIcon />}
                                                label={call.detected_errors.length}
                                                size="small"
                                                color="error"
                                              />
                                            )}
                                          </TableCell>
                                          <TableCell align="center">
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              onClick={() => handleOpenCallDetails(call)}
                                            >
                                              Детали
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography>Нет звонков</Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </>
  );

  const renderErrorAnalysisTab = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Анализ ошибок операторов
        </Typography>
        
        {loadingAnalysis ? (
          <CircularProgress />
        ) : errorAnalysis ? (
          <Box>
            {/* Errors by Category */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Ошибки по категориям
            </Typography>
            <Box sx={{ mb: 3 }}>
              {errorAnalysis.errors_by_category.map((cat) => (
                <Box key={cat.category} sx={{ mb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">
                      {categoryNameRu[cat.category] || cat.category}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {cat.count} ({cat.percent}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={cat.percent}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Most Common Errors */}
            <Typography variant="subtitle1" gutterBottom>
              Топ ошибок
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Код</TableCell>
                    <TableCell>Ошибка</TableCell>
                    <TableCell>Категория</TableCell>
                    <TableCell align="right">Кол-во</TableCell>
                    <TableCell align="right">%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorAnalysis.most_common_errors.slice(0, 10).map((err) => (
                    <TableRow key={err.code}>
                      <TableCell>
                        <Chip label={err.code} size="small" />
                      </TableCell>
                      <TableCell>{err.name}</TableCell>
                      <TableCell>{categoryNameRu[err.category] || err.category}</TableCell>
                      <TableCell align="right">{err.count}</TableCell>
                      <TableCell align="right">{err.percent}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Operators by Error */}
            <Typography variant="subtitle1" gutterBottom>
              Операторы с частыми ошибками
            </Typography>
            {errorAnalysis.operators_by_error.map((item) => (
              <Box key={item.code} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">{getErrorName(item.code)}</Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {item.operators.map((op) => (
                    <Chip
                      key={op.name}
                      label={`${op.name}: ${op.count}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Alert severity="info">Нет данных</Alert>
        )}
      </CardContent>
    </Card>
  );

  const renderTechnicalAnalysisTab = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Технические ошибки (Связь и Оборудование)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Анализ проблем со связью, громкостью, помехами и качеством оборудования операторов.
        </Typography>
        
        {loadingAnalysis ? (
          <CircularProgress />
        ) : technicalAnalysis ? (
          <Box>
            {/* Most Common Technical Issues */}
            <Typography variant="subtitle1" gutterBottom>
              Частота технических проблем
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Проблема</TableCell>
                    <TableCell align="right">Кол-во случаев</TableCell>
                    <TableCell align="right">% от всех звонков</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {technicalAnalysis.most_common_issues.length > 0 ? (
                    technicalAnalysis.most_common_issues.map((issue) => (
                      <TableRow key={issue.code}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                            <Typography variant="body2">{issue.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{issue.count}</TableCell>
                        <TableCell align="right">
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                            <Typography variant="body2">{issue.percent}%</Typography>
                            <Box sx={{ width: 100 }}>
                              <LinearProgress
                                variant="determinate"
                                value={issue.percent}
                                color="warning"
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        Технических проблем не зафиксировано
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 3 }} />

            {/* Operators by Technical Issue */}
            <Typography variant="subtitle1" gutterBottom>
              Операторы с проблемным оборудованием/связью
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              {technicalAnalysis.operators_by_issue.map((item) => (
                <Card key={item.code} variant="outlined" sx={{ p: 2, minWidth: 280, flexGrow: 1 }}>
                  <Typography variant="subtitle2" color="error.main" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {getErrorName(item.code)}
                  </Typography>
                  <List dense>
                    {item.operators.map((op) => (
                      <ListItem key={op.name} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText 
                          primary={op.name} 
                          primaryTypographyProps={{ variant: 'body2' }} 
                        />
                        <Chip label={`${op.count} раз`} size="small" variant="outlined" />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              ))}
              {technicalAnalysis.operators_by_issue.length === 0 && (
                <Alert severity="success" sx={{ width: '100%' }}>
                  У всех операторов стабильная связь и исправное оборудование
                </Alert>
              )}
            </Box>
          </Box>
        ) : (
          <Alert severity="info">Нет данных</Alert>
        )}
      </CardContent>
    </Card>
  );

  const renderObjectionsTab = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Анализ работы с возражениями
        </Typography>
        
        {loadingAnalysis ? (
          <CircularProgress />
        ) : objectionsAnalysis ? (
          <Box>
            {/* Objection Stats */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Статистика по возражениям
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Возражение</TableCell>
                    <TableCell align="right">Встречено</TableCell>
                    <TableCell align="right">Отработано</TableCell>
                    <TableCell align="right">Успех</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {objectionsAnalysis.objection_stats.map((stat) => (
                    <TableRow key={stat.objection}>
                      <TableCell>"{stat.objection}"</TableCell>
                      <TableCell align="right">{stat.total_encountered}</TableCell>
                      <TableCell align="right">{stat.total_handled}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${stat.success_rate}%`}
                          size="small"
                          color={stat.success_rate >= 60 ? 'success' : stat.success_rate >= 40 ? 'warning' : 'error'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            {/* Best Handlers */}
            <Typography variant="subtitle1" gutterBottom>
              Лучшие обработчики возражений
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              {objectionsAnalysis.best_handlers.map((handler) => (
                <Card key={handler.objection} variant="outlined" sx={{ p: 2, minWidth: 200 }}>
                  <Typography variant="body2" color="text.secondary">
                    "{handler.objection}"
                  </Typography>
                  <Typography variant="h6">{handler.operator_name}</Typography>
                  <Chip label={`${handler.success_rate}%`} color="success" size="small" />
                </Card>
              ))}
            </Box>
          </Box>
        ) : (
          <Alert severity="info">Нет данных</Alert>
        )}
      </CardContent>
    </Card>
  );

  const renderTeamDiagnosisTab = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Диагностика команды
        </Typography>
        
        {loadingAnalysis ? (
          <CircularProgress />
        ) : teamDiagnosis ? (
          <Box>
            {/* Weakness Distribution */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Распределение слабостей по этапам
            </Typography>
            <Box sx={{ mb: 3 }}>
              {teamDiagnosis.overall_weakness_distribution.map((item) => (
                <Box key={item.stage} sx={{ mb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">
                      {stageNameRu[item.stage] || item.stage}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {item.percent}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.percent}
                    sx={{ height: 8, borderRadius: 4 }}
                    color={item.percent > 30 ? 'error' : item.percent > 15 ? 'warning' : 'success'}
                  />
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Primary Issues */}
            <Typography variant="subtitle1" gutterBottom>
              Ключевые проблемы
            </Typography>
            {teamDiagnosis.primary_issues.length > 0 ? (
              <List>
                {teamDiagnosis.primary_issues.map((issue, idx) => (
                  <ListItem key={idx} sx={{ bgcolor: '#f5f5f5', mb: 1, borderRadius: 1 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip
                          label={stageNameRu[issue.stage] || issue.stage}
                          size="small"
                          sx={{ bgcolor: severityColor[issue.severity], color: 'white' }}
                        />
                        <Chip label={issue.severity} size="small" variant="outlined" />
                      </Box>
                      <Typography variant="body1" fontWeight="bold">
                        {issue.issue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Затронуто операторов: {issue.affected_operators.join(', ')}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="success">Критических проблем не выявлено</Alert>
            )}
          </Box>
        ) : (
          <Alert severity="info">Нет данных</Alert>
        )}
      </CardContent>
    </Card>
  );

  // ==================== CALL DETAILS MODAL ====================

  const renderCallDetailsModal = () => (
    <Dialog
      open={callModalOpen}
      onClose={() => setCallModalOpen(false)}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Детали звонка #{selectedCall?.call_id}
        {isMobile && (
          <IconButton
            onClick={() => setCallModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ p: isMobile ? 1 : 2 }}>
        {selectedCall && (
          <Box>
            {/* Call Outcome */}
            {selectedCall.call_outcome && (
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={callOutcomeNames[selectedCall.call_outcome]?.label || selectedCall.call_outcome}
                  sx={{ 
                    bgcolor: callOutcomeNames[selectedCall.call_outcome]?.color || '#9e9e9e',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {callOutcomeNames[selectedCall.call_outcome]?.description}
                </Typography>
              </Box>
            )}

            {/* AI Recommendation */}
            {selectedCall.ai_recommendation && (
              <Alert severity="info" icon={<LightbulbIcon />} sx={{ mb: 2 }}>
                <strong>Рекомендация ИИ:</strong> {selectedCall.ai_recommendation}
              </Alert>
            )}

            {/* Primary Weakness - only show if there was a real weakness */}
            {selectedCall.primary_weakness && selectedCall.call_outcome !== 'client_busy' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Основная слабость ({stageNameRu[selectedCall.weakness_stage || ''] || selectedCall.weakness_stage}):</strong>{' '}
                {selectedCall.primary_weakness}
              </Alert>
            )}

            {/* Dropout Stage - show with context */}
            {selectedCall.dropout_stage && (
              <Alert 
                severity={selectedCall.call_outcome === 'client_busy' ? 'info' : 'error'} 
                sx={{ mb: 2 }}
              >
                <strong>
                  {selectedCall.call_outcome === 'client_busy' 
                    ? 'Разговор завершился на этапе:' 
                    : 'Клиент отвалился на этапе:'}
                </strong>{' '}
                {stageNameRu[selectedCall.dropout_stage] || selectedCall.dropout_stage}
                {selectedCall.call_outcome === 'client_busy' && (
                  <Typography variant="caption" display="block">
                    (Клиент был занят — это не ошибка оператора)
                  </Typography>
                )}
              </Alert>
            )}

            {/* Detected Errors */}
            {selectedCall.detected_errors?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Обнаруженные ошибки:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {selectedCall.detected_errors.map((code) => (
                    <Tooltip key={code} title={code}>
                      <Chip label={getErrorName(code)} color="error" size="small" />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}

            {/* Technical Issues */}
            {selectedCall.technical_issues?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Технические проблемы:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {selectedCall.technical_issues.map((code) => (
                    <Tooltip key={code} title={code}>
                      <Chip label={getErrorName(code)} color="warning" size="small" />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}

            {/* Objections */}
            {selectedCall.objections_encountered?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Возражения клиента:
                </Typography>
                {selectedCall.objections_encountered.map((obj, idx) => (
                  <Chip
                    key={idx}
                    label={obj}
                    size="small"
                    sx={{ mr: 1, mb: 0.5 }}
                    color={selectedCall.objections_handled?.includes(obj) ? 'success' : 'default'}
                    variant={selectedCall.objections_handled?.includes(obj) ? 'filled' : 'outlined'}
                  />
                ))}
                <Typography variant="caption" display="block" color="text.secondary">
                  Зеленый = отработано {selectedCall.first_refusal_handled && "(Первое возражение удержано)"}
                </Typography>
              </Box>
            )}

            {/* Communication Metrics */}
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0f4f8', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Метрики коммуникации:
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Talk Ratio (Опер.)</Typography>
                  <Typography variant="body2" fontWeight="bold">{formatScore(selectedCall.talk_ratio_operator, 0)}%</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Перебивания</Typography>
                  <Typography variant="body2" fontWeight="bold" color={selectedCall.interruptions_count > 0 ? 'error.main' : 'inherit'}>
                    {selectedCall.interruptions_count}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Слова-паразиты / 100</Typography>
                  <Typography variant="body2" fontWeight="bold">{formatScore(selectedCall.filler_words_per_100, 1)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Соблюдение этикета</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedCall.politeness_markers_present ? 'Да' : 'Нет'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Следующий шаг</Typography>
                  <Typography variant="body2" fontWeight="bold" color={selectedCall.next_step_recorded ? 'success.main' : 'error.main'}>
                    {selectedCall.next_step_recorded ? 'Зафиксирован' : 'Не зафиксирован'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Checklist Result */}
            {selectedCall.checklist_result && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Чек-лист квалификации:
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <LinearProgress
                    variant="determinate"
                    value={(selectedCall.checklist_result.fulfilled / selectedCall.checklist_result.total) * 100}
                    sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                  />
                  <Typography variant="body2">
                    {selectedCall.checklist_result.fulfilled}/{selectedCall.checklist_result.total}
                  </Typography>
                </Box>
                {selectedCall.checklist_result.missing?.length > 0 && (
                  <Typography variant="caption" color="error">
                    Не уточнено: {selectedCall.checklist_result.missing.join(', ')}
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Stage Scores */}
            <Typography variant="subtitle2" gutterBottom>
              Оценки по этапам:
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Приветствие</TableCell>
                  <TableCell sx={{ color: getScoreColor(selectedCall.greeting_score), fontWeight: 'bold' }}>
                    {formatScore(selectedCall.greeting_score, 0)}
                  </TableCell>
                  <TableCell>{selectedCall.greeting_explanation}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Удержание</TableCell>
                  <TableCell sx={{ 
                    color: selectedCall.retention_score === null ? '#9e9e9e' : getScoreColor(selectedCall.retention_score), 
                    fontWeight: 'bold' 
                  }}>
                    {selectedCall.retention_score === null ? '—' : formatScore(selectedCall.retention_score, 0)}
                  </TableCell>
                  <TableCell sx={{ color: selectedCall.retention_score === null ? '#9e9e9e' : 'inherit' }}>
                    {selectedCall.retention_explanation || (selectedCall.retention_score === null ? 'Этап не состоялся' : '')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Потребности</TableCell>
                  <TableCell sx={{ 
                    color: selectedCall.needs_identification_score === null ? '#9e9e9e' : getScoreColor(selectedCall.needs_identification_score), 
                    fontWeight: 'bold' 
                  }}>
                    {selectedCall.needs_identification_score === null ? '—' : formatScore(selectedCall.needs_identification_score, 0)}
                  </TableCell>
                  <TableCell sx={{ color: selectedCall.needs_identification_score === null ? '#9e9e9e' : 'inherit' }}>
                    {selectedCall.needs_identification_explanation || (selectedCall.needs_identification_score === null ? 'Этап не состоялся' : '')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Возражения</TableCell>
                  <TableCell sx={{ 
                    color: selectedCall.objection_handling_score === null ? '#9e9e9e' : getScoreColor(selectedCall.objection_handling_score), 
                    fontWeight: 'bold' 
                  }}>
                    {selectedCall.objection_handling_score === null ? '—' : formatScore(selectedCall.objection_handling_score, 0)}
                  </TableCell>
                  <TableCell sx={{ color: selectedCall.objection_handling_score === null ? '#9e9e9e' : 'inherit' }}>
                    {selectedCall.objection_handling_explanation || (selectedCall.objection_handling_score === null ? 'Возражений не было' : '')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Презентация</TableCell>
                  <TableCell sx={{ 
                    color: selectedCall.presentation_score === null ? '#9e9e9e' : getScoreColor(selectedCall.presentation_score), 
                    fontWeight: 'bold' 
                  }}>
                    {selectedCall.presentation_score === null ? '—' : formatScore(selectedCall.presentation_score, 0)}
                  </TableCell>
                  <TableCell sx={{ color: selectedCall.presentation_score === null ? '#9e9e9e' : 'inherit' }}>
                    {selectedCall.presentation_explanation || (selectedCall.presentation_score === null ? 'Этап не состоялся' : '')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Качество коммуникации</TableCell>
                  <TableCell sx={{ 
                    color: selectedCall.communication_quality_score === null ? '#9e9e9e' : getScoreColor(selectedCall.communication_quality_score), 
                    fontWeight: 'bold' 
                  }}>
                    {selectedCall.communication_quality_score === null ? '—' : formatScore(selectedCall.communication_quality_score, 0)}
                  </TableCell>
                  <TableCell sx={{ color: selectedCall.communication_quality_score === null ? '#9e9e9e' : 'inherit' }}>
                    {selectedCall.communication_quality_explanation || (selectedCall.communication_quality_score === null ? 'Этап не состоялся' : '')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Закрытие</TableCell>
                  <TableCell sx={{ 
                    color: selectedCall.closing_score === null ? '#9e9e9e' : getScoreColor(selectedCall.closing_score), 
                    fontWeight: 'bold' 
                  }}>
                    {selectedCall.closing_score === null ? '—' : formatScore(selectedCall.closing_score, 0)}
                  </TableCell>
                  <TableCell sx={{ color: selectedCall.closing_score === null ? '#9e9e9e' : 'inherit' }}>
                    {selectedCall.closing_explanation || (selectedCall.closing_score === null ? 'Этап не состоялся' : '')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCallModalOpen(false)}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );

  // ==================== MAIN RENDER ====================

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
        Контроль качества V2
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Typography variant="h6" gutterBottom>
            Фильтры
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch', flexDirection: isMobile ? 'column' : 'row' }}>
            <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
              <TextField
                label="С"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="По"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
            <ProjectAutocomplete
              projects={projects}
              value={selectedProjectId}
              onChange={(val) => setSelectedProjectId(val ? Number(val) : null)}
              sx={{ minWidth: isMobile ? '100%' : 200 }}
              size="small"
            />
            <Stack direction="row" spacing={2} sx={{ width: isMobile ? '100%' : 'auto' }}>
              <Button variant="contained" onClick={handleApplyFilters} sx={{ flex: 1 }}>
                {isMobile ? 'ОК' : 'Применить'}
              </Button>
              <Button variant="outlined" onClick={handleResetFilters} sx={{ flex: 1 }}>
                Сброс
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={(_, v) => setActiveTab(v)} 
        sx={{ mb: 2 }}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons="auto"
      >
        <Tab label="Операторы" />
        <Tab label="Анализ ошибок" />
        <Tab label="Технические" />
        <Tab label="Возражения" />
        <Tab label="Команда" />
      </Tabs>

      {/* Tab Content */}
      {activeTab === 0 && renderOperatorsTab()}
      {activeTab === 1 && renderErrorAnalysisTab()}
      {activeTab === 2 && renderTechnicalAnalysisTab()}
      {activeTab === 3 && renderObjectionsTab()}
      {activeTab === 4 && renderTeamDiagnosisTab()}

      {/* No data */}
      {!loading && activeTab === 0 && operators.length === 0 && (
        <Alert severity="info">Нет данных за выбранный период</Alert>
      )}

      {/* Call Details Modal */}
      {renderCallDetailsModal()}
    </Box>
  );
};
